<!--
/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */
 -->

<template>
  <div class="orangehrm-action-buttons-container expensa-actions">
    <oxd-button
      v-if="actions.canDelete"
      display-type="danger"
      class="orangehrm-sm-button"
      :label="$t('expensa.action_delete')"
      :disabled="busy"
      @click="onDelete"
    />
    <oxd-button
      display-type="ghost"
      class="orangehrm-sm-button"
      :label="$t('expensa.action_comment')"
      :disabled="busy"
      @click="onComment"
    />
    <oxd-button
      v-if="actions.canReopen"
      display-type="ghost"
      class="orangehrm-sm-button"
      :label="$t('expensa.action_reopen')"
      :disabled="busy"
      @click="onReopen"
    />
    <oxd-button
      v-if="actions.canReject"
      display-type="danger"
      class="orangehrm-sm-button"
      :label="$t('expensa.action_reject')"
      :disabled="busy"
      @click="onReject"
    />
    <oxd-button
      v-if="actions.canApprove"
      display-type="secondary"
      class="orangehrm-sm-button"
      :label="$t('expensa.action_approve')"
      :disabled="busy"
      @click="onApprove"
    />
    <oxd-button
      v-if="actions.canSubmit"
      display-type="secondary"
      class="orangehrm-sm-button"
      :label="$t(actions.submitLabelKey)"
      :disabled="busy"
      @click="onSubmit"
    />
  </div>

  <comment-dialog
    ref="optionalComment"
    :title="commentTitle"
    :confirm-label="commentConfirm"
  />
  <comment-dialog
    ref="requiredComment"
    required
    :title="$t('expensa.action_reject')"
    :confirm-label="$t('expensa.action_reject')"
  />
  <confirmation-dialog
    ref="reopenDialog"
    :title="$t('expensa.confirm_reopen_title')"
    :subtitle="$t('expensa.confirm_reopen_text')"
    :cancel-label="$t('general.cancel')"
    :confirm-label="$t('expensa.action_reopen')"
    confirm-button-type="secondary"
  />
  <confirmation-dialog
    ref="deleteDialog"
    :title="$t('expensa.confirm_delete_title')"
    :subtitle="$t('expensa.confirm_delete_text')"
    :cancel-label="$t('general.cancel')"
    :confirm-label="$t('expensa.action_delete')"
  />
</template>

<script>
import {computed, ref} from 'vue';
import ConfirmationDialog from '@/core/components/dialogs/ConfirmationDialog.vue';
import CommentDialog from '@/orangehrmExpensaPlugin/components/CommentDialog.vue';
import {expensaApi, errorMessage, pageUrl} from '../util/api';
import {reportActions} from '../util/reportActions';

// Le azioni sulla nota. Cosa si può fare lo dice solo `permissions`; ogni
// azione usa il dettaglio restituito dalla risposta e lo passa a `updated`.
export default {
  name: 'ExpensaReportActionsBar',
  components: {
    'comment-dialog': CommentDialog,
    'confirmation-dialog': ConfirmationDialog,
  },
  props: {
    report: {
      type: Object,
      required: true,
    },
  },
  emits: ['updated'],
  setup(props) {
    const commentTitle = ref('');
    const commentConfirm = ref('');
    const actions = computed(() => reportActions(props.report));
    return {actions, commentTitle, commentConfirm};
  },
  data() {
    return {busy: false};
  },
  methods: {
    async run(call, successKey) {
      this.busy = true;
      try {
        const {data} = await call();
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t(successKey),
        });
        this.$emit('updated', data.data);
      } catch (e) {
        this.$toast.error({
          title: this.$t('general.error'),
          message: errorMessage(e),
        });
      } finally {
        this.busy = false;
      }
    },
    act(action, comment) {
      return expensaApi(`/reports/${this.report.id}/action`).request({
        method: 'PUT',
        data: {action, ...(comment ? {comment} : {})},
      });
    },
    async askOptional(titleKey) {
      this.commentTitle = this.$t(titleKey);
      this.commentConfirm = this.$t(titleKey);
      await this.$nextTick();
      return this.$refs.optionalComment.showDialog();
    },
    async onSubmit() {
      const comment = await this.askOptional(this.actions.submitLabelKey);
      if (comment === null) return;
      await this.run(() => this.act('SUBMIT', comment), 'expensa.toast_done');
    },
    async onApprove() {
      const comment = await this.askOptional('expensa.action_approve');
      if (comment === null) return;
      await this.run(() => this.act('APPROVE', comment), 'expensa.toast_done');
    },
    async onReject() {
      const comment = await this.$refs.requiredComment.showDialog();
      if (comment === null) return;
      await this.run(() => this.act('REJECT', comment), 'expensa.toast_done');
    },
    async onReopen() {
      const choice = await this.$refs.reopenDialog.showDialog();
      if (choice !== 'ok') return;
      await this.run(() => this.act('REOPEN'), 'expensa.toast_done');
    },
    async onComment() {
      const comment = await this.askOptional('expensa.action_comment');
      if (comment === null || comment === '') return;
      await this.run(() => this.act('COMMENT', comment), 'expensa.toast_done');
    },
    async onDelete() {
      const choice = await this.$refs.deleteDialog.showDialog();
      if (choice !== 'ok') return;
      this.busy = true;
      try {
        await expensaApi('/reports').delete(this.report.id);
        this.$toast.success({
          title: this.$t('general.success'),
          message: this.$t('expensa.toast_deleted'),
        });
        window.location.href = pageUrl('viewReports');
      } catch (e) {
        this.busy = false;
        this.$toast.error({
          title: this.$t('general.error'),
          message: errorMessage(e),
        });
      }
    },
  },
};
</script>

<style scoped lang="scss">
.expensa-actions {
  flex-wrap: wrap;
}
</style>
